import type { CandidateRecord } from '@/lib/api/schemas';

// Where the applicant is in the questionnaire. Today this is derived client-side from the
// hub-contract Record (the one place that inference lives); when candidate-service exposes
// the workflow API the server returns it and `stepFromRecord` goes away.
export type Step =
  | 'welcome'
  | 'profile'
  | 'work_type'
  | 'work_background'
  | 'availability'
  | 'contact'
  | 'contact_waiting';

export type Destination =
  { kind: 'step'; step: Step } | { kind: 'waiting_room' } | { kind: 'no_recent_application' };

const answered = (v: unknown): boolean => v !== undefined;

export function stepFromRecord(r: CandidateRecord): Destination {
  if (r.hasRecentApplication === false) return { kind: 'no_recent_application' };
  switch (r.screeningStatus) {
    case 'New':
      return { kind: 'step', step: 'welcome' };
    case 'Waiting Room':
      return { kind: 'step', step: 'contact_waiting' };
    case 'Passed':
    case 'Failed':
      return { kind: 'waiting_room' };
    default:
      break;
  }
  if (answered(r.desiredHours) || answered(r.desiredShifts) || answered(r.desiredWorkday)) {
    return { kind: 'step', step: 'contact' };
  }
  if (
    answered(r.experience) ||
    answered(r.licenseHit) ||
    answered(r.noStateLicenseNumber) ||
    answered(r.otherStatesInterest) ||
    answered(r.previousWorkSetting) ||
    answered(r.stateLicensesHeld) ||
    answered(r.workAuthorized)
  ) {
    return { kind: 'step', step: 'availability' };
  }
  if (r.profession.id != null || answered(r.otherWorkTypeInterest) || answered(r.primaryWorkType)) {
    return { kind: 'step', step: 'work_background' };
  }
  return { kind: 'step', step: 'welcome' };
}

/** After a save: the service has re-run screening; Waiting Room short-circuits to the off-ramp. */
export function nextAfterSave(saved: CandidateRecord, completed: Step): Destination {
  if (saved.screeningStatus === 'Waiting Room') return { kind: 'step', step: 'contact_waiting' };
  switch (completed) {
    case 'profile':
      return { kind: 'step', step: 'work_type' };
    case 'work_type':
      return { kind: 'step', step: 'work_background' };
    case 'work_background':
      return { kind: 'step', step: 'availability' };
    case 'availability':
      return { kind: 'step', step: 'contact' };
    case 'contact':
    case 'contact_waiting':
      return { kind: 'waiting_room' };
    case 'welcome':
      return { kind: 'step', step: 'work_type' };
  }
}

/**
 * The old hub's step numbers, sent as `Pageview {onboardingStep}`. The GA4 funnel and a
 * Google Ads conversion (step 5) in the shared GTM container key on these exact strings.
 */
export const ANALYTICS_STEP: Readonly<Record<Step, string>> = {
  welcome: 'step 0',
  profile: 'step 1',
  work_type: 'step 2',
  work_background: 'step 3',
  availability: 'step 4',
  contact: 'step 5',
  contact_waiting: 'step 6',
};

export function isStep(value: string): value is Step {
  return Object.hasOwn(ANALYTICS_STEP, value);
}

export const PROGRESS_STEPS: readonly { step: Step; label: string }[] = [
  { step: 'profile', label: 'Account basics' },
  { step: 'work_type', label: 'Getting to know you' },
  { step: 'work_background', label: 'Work background' },
  { step: 'availability', label: 'Availability' },
];

export function progressIndex(step: Step): number {
  return PROGRESS_STEPS.findIndex((s) => s.step === step);
}

export function destinationPath(to: Destination): string {
  switch (to.kind) {
    case 'step':
      return `/apply/${to.step}`;
    case 'waiting_room':
      return '/waitingRoom';
    case 'no_recent_application':
      return '/no-recent-application';
  }
}
